/*
|--------------------------------------------------------------------------
| Decorator Schemas
|--------------------------------------------------------------------------
|
| Joi validation schemas for Resourceful decorators. These schemas ensure
| that decorator options to the expected shapes during runtime.
|
*/
import { joi } from '@nhtio/lucid-resourceful/joi'
import { guards } from '@nhtio/lucid-resourceful/utils'
import { E_INVALID_RESOURCEFUL_DECORATOR_OPTIONS } from '@nhtio/lucid-resourceful/errors'
import {
  ResourcefulStringType,
  ResourcefulDateType,
  ResourcefulDateTimeType,
  ResourcefulBinaryType,
  ResourcefulNumberType,
  ResourcefulIntegerType,
  ResourcefulBigintType,
  ResourcefulUnsignedIntegerType,
  ResourcefulBooleanType,
  ResourcefulObjectType,
  ResourcefulArrayType,
} from '@nhtio/lucid-resourceful/definitions'
import type { ObjectSchema } from '@nhtio/validation'

/*
|--------------------------------------------------------------------------
| Validate, Normalize and Cast
|--------------------------------------------------------------------------
|
| Internal utility for validating decorator options using Joi schemas.
| Applies defaults, performs type conversions, and provides meaningful
| error messages with context about which decorator and field failed.
|
*/

const joiInstanceCheckerAlternativesFactory = (Ctor: any, CtorName: string) => {
  return [
    joi.object().instance(Ctor),
    joi.function().instance(Ctor),
    joi.custom((value, helpers) => {
      if (guards.isObject(value) && value.constructor.name === CtorName) {
        return value
      }
      return helpers.error('any.invalid', { value, type: CtorName })
    }),
  ]
}

export const validateNormalizeAndCastDecoratorOptions = <InputType, OutputType>(
  fieldName: string,
  decoratorName: string,
  schema: ObjectSchema,
  input: InputType
): OutputType => {
  const { error, value } = schema.validate(input, {
    convert: true,
    abortEarly: false,
  })
  if (error) {
    throw new E_INVALID_RESOURCEFUL_DECORATOR_OPTIONS(decoratorName, fieldName, error)
  }
  return value as OutputType
}

/*
|--------------------------------------------------------------------------
| Base Schemas
|--------------------------------------------------------------------------
*/

const resourcefulPropertyDefinitionSchema = joi.object({
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
})

const resourcefulDataTypeSchema = joi.alternatives(
  ...joiInstanceCheckerAlternativesFactory(ResourcefulStringType, 'ResourcefulStringType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulDateType, 'ResourcefulDateType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulDateTimeType, 'ResourcefulDateTimeType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulBinaryType, 'ResourcefulBinaryType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulNumberType, 'ResourcefulNumberType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulIntegerType, 'ResourcefulIntegerType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulBigintType, 'ResourcefulBigintType'),
  ...joiInstanceCheckerAlternativesFactory(
    ResourcefulUnsignedIntegerType,
    'ResourcefulUnsignedIntegerType'
  ),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulBooleanType, 'ResourcefulBooleanType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulObjectType, 'ResourcefulObjectType'),
  ...joiInstanceCheckerAlternativesFactory(ResourcefulArrayType, 'ResourcefulArrayType')
)

const localResourcefulPropertyDefinitionSchema = resourcefulPropertyDefinitionSchema.keys({
  type: resourcefulDataTypeSchema.required(),
  /** Whether this column can be used in filter operations. Defaults to true. */
  filterable: joi.boolean().default(true),
  /** Whether this column can be used in sort operations. Defaults to true. */
  sortable: joi.boolean().default(true),
  /** Whether this column can be used in aggregation operations. Defaults to false. */
  aggregatable: joi.boolean().default(false),
  /** Whether this column is immutable. Defaults to false. */
  immutable: joi.boolean().default(false),
  /** The property name of the relationship which this column acts as a key for */
  relationshipKey: joi.string().optional().allow('', null),
})

/*
|--------------------------------------------------------------------------
| Column Schemas
|--------------------------------------------------------------------------
*/

export const resourcefulColumnDefinitionSchema = localResourcefulPropertyDefinitionSchema

export const resourcefulColumnOptionsSchema = joi.object({
  // Lucid ColumnOptions (partial)
  columnName: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  consume: joi.function().optional(),
  prepare: joi.function().optional(),
  meta: joi.object().optional(),
  isPrimary: joi.boolean().default(false),
  hasDefaultValue: joi.boolean().default(false),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: resourcefulDataTypeSchema.required(),
  /** Whether this column can be used in filter operations. Defaults to true. */
  filterable: joi.boolean().default(true),
  /** Whether this column can be used in sort operations. Defaults to true. */
  sortable: joi.boolean().default(true),
  /** Whether this column can be used in aggregation operations. Defaults to false. */
  aggregatable: joi.boolean().default(false),
  /** Whether this column is immutable. Defaults to false. */
  immutable: joi.boolean().default(false),
  /** The property name of the relationship which this column acts as a key for */
  relationshipKey: joi.string().optional().allow('', null),
})

/*
|--------------------------------------------------------------------------
| Data Type Specific Column Schemas
|--------------------------------------------------------------------------
*/

export const dataTypeColumnOptionsSchema = joi.object({
  // Lucid ColumnOptions (full) - now including 'prepare' and 'consume'
  columnName: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  consume: joi.function().optional(),
  prepare: joi.function().optional(),
  meta: joi.object().optional(),
  isPrimary: joi.boolean().default(false),
  hasDefaultValue: joi.boolean().default(false),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: resourcefulDataTypeSchema.optional(),
  /** Whether this column can be used in filter operations. Defaults to true. */
  filterable: joi.boolean().default(true),
  /** Whether this column can be used in sort operations. Defaults to true. */
  sortable: joi.boolean().default(true),
  /** Whether this column can be used in aggregation operations. Defaults to false. */
  aggregatable: joi.boolean().default(false),
  /** Whether this column is immutable. Defaults to false. */
  immutable: joi.boolean().default(false),
  /** The property name of the relationship which this column acts as a key for */
  relationshipKey: joi.string().optional().allow('', null),
})

const baseDateColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  autoCreate: joi.boolean().default(false),
  autoUpdate: joi.boolean().default(false),
})

/*
|--------------------------------------------------------------------------
| String Column Schema
|--------------------------------------------------------------------------
*/

export const stringColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulStringType, 'ResourcefulStringType')
    )
    .default(
      (parent: any) =>
        new ResourcefulStringType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Date Column Schemas
|--------------------------------------------------------------------------
*/

export const dateColumnOptionsSchema = baseDateColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulDateType, 'ResourcefulDateType')
    )
    .default(
      (parent: any) =>
        new ResourcefulDateType({
          nullable: parent.nullable,
          autoCreate: parent.autoCreate,
          autoUpdate: parent.autoUpdate,
        })
    ),
})

export const dateTimeColumnOptionsSchema = baseDateColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulDateTimeType, 'ResourcefulDateTimeType')
    )
    .default(
      (parent: any) =>
        new ResourcefulDateTimeType({
          nullable: parent.nullable,
          autoCreate: parent.autoCreate,
          autoUpdate: parent.autoUpdate,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Binary Column Schema
|--------------------------------------------------------------------------
*/

export const binaryColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBinaryType, 'ResourcefulBinaryType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBinaryType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Number Column Schemas
|--------------------------------------------------------------------------
*/

export const numberColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulNumberType, 'ResourcefulNumberType')
    )
    .default(
      (parent: any) =>
        new ResourcefulNumberType({
          nullable: parent.nullable,
        })
    ),
})

export const integerColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulIntegerType, 'ResourcefulIntegerType')
    )
    .default(
      (parent: any) =>
        new ResourcefulIntegerType({
          nullable: parent.nullable,
        })
    ),
})

export const bigintColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBigintType, 'ResourcefulBigintType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBigintType({
          nullable: parent.nullable,
        })
    ),
})

export const unsignedIntegerColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(
        ResourcefulUnsignedIntegerType,
        'ResourcefulUnsignedIntegerType'
      )
    )
    .default(
      (parent: any) =>
        new ResourcefulUnsignedIntegerType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Boolean Column Schema
|--------------------------------------------------------------------------
*/

export const booleanColumnOptionsSchema = dataTypeColumnOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBooleanType, 'ResourcefulBooleanType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBooleanType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Object/Array Column Schemas
|--------------------------------------------------------------------------
*/

export const objectColumnOptionsSchema = joi.object({
  // Lucid ColumnOptions (full)
  columnName: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  consume: joi.function().optional(),
  prepare: joi.function().optional(),
  meta: joi.object().optional(),
  isPrimary: joi.boolean().default(false),
  hasDefaultValue: joi.boolean().default(false),

  // Immutability options
  immutable: joi.boolean().default(false),

  // Resourceful options
  propertyKey: joi.string().default(''),
  relationshipKey: joi.string().optional().allow('', null),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulObjectType, 'ResourcefulObjectType')
    )
    .default(
      (parent: any) =>
        new ResourcefulObjectType({
          nullable: parent.nullable,
        })
    ),
})

export const arrayColumnOptionsSchema = joi.object({
  // Lucid ColumnOptions (full)
  columnName: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  consume: joi.function().optional(),
  prepare: joi.function().optional(),
  meta: joi.object().optional(),
  isPrimary: joi.boolean().default(false),
  hasDefaultValue: joi.boolean().default(false),

  // Immutability options
  immutable: joi.boolean().default(false),

  // Resourceful options
  propertyKey: joi.string().default(''),
  relationshipKey: joi.string().optional().allow('', null),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulArrayType, 'ResourcefulArrayType')
    )
    .default(
      (parent: any) =>
        new ResourcefulArrayType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Computed Schemas
|--------------------------------------------------------------------------
*/

export const resourcefulComputedDefinitionSchema = localResourcefulPropertyDefinitionSchema.append({
  writable: joi.boolean().default(false),
})

export const resourcefulComputedOptionsSchema = joi.object({
  // Lucid ComputedOptions (partial)
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  meta: joi.object().optional(),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: resourcefulDataTypeSchema.required(),
  writable: joi.boolean().default(false),
})

/*
|--------------------------------------------------------------------------
| Data Type Specific Computed Schemas
|--------------------------------------------------------------------------
*/

export const dataTypeComputedOptionsSchema = joi.object({
  // Lucid ComputedOptions (partial) - excluding 'prepare' and 'consume'
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  meta: joi.object().optional(),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: resourcefulDataTypeSchema.optional(),
  writeable: joi.boolean().default(false),
})

const baseDateComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  autoCreate: joi.boolean().default(false),
  autoUpdate: joi.boolean().default(false),
})

/*
|--------------------------------------------------------------------------
| String Computed Schema
|--------------------------------------------------------------------------
*/

export const stringComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulStringType, 'ResourcefulStringType')
    )
    .default(
      (parent: any) =>
        new ResourcefulStringType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Date Computed Schemas
|--------------------------------------------------------------------------
*/

export const dateComputedOptionsSchema = baseDateComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulDateType, 'ResourcefulDateType')
    )
    .default(
      (parent: any) =>
        new ResourcefulDateType({
          nullable: parent.nullable,
        })
    ),
})

export const dateTimeComputedOptionsSchema = baseDateComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulDateTimeType, 'ResourcefulDateTimeType')
    )
    .default(
      (parent: any) =>
        new ResourcefulDateTimeType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Binary Computed Schema
|--------------------------------------------------------------------------
*/

export const binaryComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBinaryType, 'ResourcefulBinaryType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBinaryType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Number Computed Schemas
|--------------------------------------------------------------------------
*/

export const numberComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulNumberType, 'ResourcefulNumberType')
    )
    .default(
      (parent: any) =>
        new ResourcefulNumberType({
          nullable: parent.nullable,
        })
    ),
})

export const integerComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulIntegerType, 'ResourcefulIntegerType')
    )
    .default(
      (parent: any) =>
        new ResourcefulIntegerType({
          nullable: parent.nullable,
        })
    ),
})

export const bigintComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBigintType, 'ResourcefulBigintType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBigintType({
          nullable: parent.nullable,
        })
    ),
})

export const unsignedIntegerComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(
        ResourcefulUnsignedIntegerType,
        'ResourcefulUnsignedIntegerType'
      )
    )
    .default(
      (parent: any) =>
        new ResourcefulUnsignedIntegerType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Boolean Computed Schema
|--------------------------------------------------------------------------
*/

export const booleanComputedOptionsSchema = dataTypeComputedOptionsSchema.keys({
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulBooleanType, 'ResourcefulBooleanType')
    )
    .default(
      (parent: any) =>
        new ResourcefulBooleanType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Object/Array Computed Schemas
|--------------------------------------------------------------------------
*/

export const objectComputedOptionsSchema = joi.object({
  // Lucid ComputedOptions (full)
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  meta: joi.object().optional(),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulObjectType, 'ResourcefulObjectType')
    )
    .default(
      (parent: any) =>
        new ResourcefulObjectType({
          nullable: parent.nullable,
        })
    ),
})

export const arrayComputedOptionsSchema = joi.object({
  // Lucid ComputedOptions (full)
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  serialize: joi.function().optional(),
  meta: joi.object().optional(),

  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
  type: joi
    .alternatives(
      ...joiInstanceCheckerAlternativesFactory(ResourcefulArrayType, 'ResourcefulArrayType')
    )
    .default(
      (parent: any) =>
        new ResourcefulArrayType({
          nullable: parent.nullable,
        })
    ),
})

/*
|--------------------------------------------------------------------------
| Relationship Schemas
|--------------------------------------------------------------------------
|
| Joi validation schemas for relationship decorator options. These schemas
| combine Lucid relationship options with resourceful relationship options.
|
*/

// Base schema for resourceful relationship options
export const resourcefulRelationshipOptionsSchema = joi.object({
  // Resourceful options
  propertyKey: joi.string().default(''),
  readAccessControlFilters: joi.array().items(joi.function()).default([]),
  writeAccessControlFilters: joi.array().items(joi.function()).default([]),
  nullable: joi.boolean().default(false),
  description: joi.string().optional(),
  default: joi.any().optional(),
  externalDocs: joi
    .object({
      description: joi.string().optional(),
      url: joi.string().uri().required(),
    })
    .optional(),
  example: joi.string().optional(),
  deprecated: joi.boolean().default(false),
})

// Schema for belongsTo, hasOne, hasMany relationships
export const relationOptionsSchema = resourcefulRelationshipOptionsSchema.keys({
  // Lucid RelationOptions
  localKey: joi.string().optional(),
  foreignKey: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  onQuery: joi.function().optional(),
  relatedModel: joi.function(),
})

// Schema for manyToMany relationships
export const manyToManyRelationOptionsSchema = resourcefulRelationshipOptionsSchema.keys({
  // Lucid ManyToManyRelationOptions
  localKey: joi.string().optional(),
  relatedKey: joi.string().optional(),
  pivotForeignKey: joi.string().optional(),
  pivotRelatedForeignKey: joi.string().optional(),
  pivotTable: joi.string().optional(),
  pivotTimestamps: joi.boolean().optional(),
  pivotColumns: joi.array().items(joi.string()).optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  onQuery: joi.function().optional(),
  relatedModel: joi.function(),
})

// Schema for hasManyThrough relationships
export const hasManyThroughRelationOptionsSchema = resourcefulRelationshipOptionsSchema.keys({
  // Lucid HasManyThroughRelationOptions (ThroughRelationOptions without throughModel)
  localKey: joi.string().optional(),
  foreignKey: joi.string().optional(),
  throughLocalKey: joi.string().optional(),
  throughForeignKey: joi.string().optional(),
  serializeAs: joi.alternatives().try(joi.string(), joi.valid(null)).optional(),
  onQuery: joi.function().optional(),
  relatedModel: joi.function(),
})
